import { useGetUser } from './useGetUser';
import { useGetUserGroups } from './useGetUserGroups';
import { useGetGroups } from '../../GroupList/hooks/useGetGroups';
import { GroupDto, GroupsApi, UserDto, UsersApi } from '../../../api-client';
import { ErrorResponse } from '../../../common/types/errorRespose';

type useUserDetailsQueriesReturn = {
  isLoading: boolean;
  fetchError: ErrorResponse | null;
  userData: UserDto | undefined;
  effectiveGroups: (GroupDto | undefined)[] | undefined;
  groups: (GroupDto & { documentId: string })[] | undefined;
};

type UseUserDetailsQueries = (
  usersApi: UsersApi,
  groupsApi: GroupsApi,
  tenantId: string,
  id: string,
) => useUserDetailsQueriesReturn;

export const useUserDetailsQueries: UseUserDetailsQueries = (usersApi, groupsApi, tenantId, id) => {
  const {
    data: userData,
    isFetching: isUserLoading,
    error: userFetchError,
  } = useGetUser(usersApi, id, {
    keepPreviousData: false,
    retry: false,
  });

  const {
    data: unparsedEffectiveGroups,
    isFetching: isUserGroupsLoading,
    error: userGroupsFetchError,
  } = useGetUserGroups(usersApi, id);

  const {
    data: groups,
    isFetching: isGroupsLoading,
    error: groupsFetchError,
  } = useGetGroups(groupsApi, tenantId, () => void 0);

  const effectiveGroups = unparsedEffectiveGroups
    ?.map(({ userGroupDocumentId, documentId }) => {
      const group = groups?.find(({ id }) => id === userGroupDocumentId);
      return group ? { ...group, documentId } : null;
    })
    .filter((i) => !!i);

  return {
    isLoading: isUserLoading || isUserGroupsLoading || isGroupsLoading,
    fetchError: (userFetchError ||
      userGroupsFetchError ||
      groupsFetchError) as ErrorResponse | null,
    userData,
    effectiveGroups,
    groups,
  } as useUserDetailsQueriesReturn;
};
